import React from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  XCircle, 
  RefreshCcw, 
  ArrowLeft,
  AlertTriangle,
  HelpCircle
} from 'lucide-react';
import Navbar from '../../components/Navbar';

const PaymentFailed = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { transactionId, paymentDetails, error } = location.state || {};

  if (!transactionId) {
    return navigate('/');
  }

  const handleRetry = () => {
    navigate('/payment', { 
      state: { packageDetails: paymentDetails }
    });
  };

  const handleSupport = () => {
    navigate('/help-and-support');
  };

  return (
    <>
    <Navbar/>
    <div className="min-h-screen bg-[#fafafa] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-red-100/30 to-rose-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-r from-rose-100/30 to-red-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 px-4 py-16 mx-auto max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-8 border border-red-100"
        >
          <div className="space-y-6 text-center">
            {/* Error Icon */}
            <div className="flex justify-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ 
                  type: "spring",
                  stiffness: 260,
                  damping: 20 
                }}
                className="flex justify-center items-center w-24 h-24 bg-gradient-to-r from-red-500 to-rose-500 rounded-full"
              >
                <XCircle className="w-12 h-12 text-white" />
              </motion.div>
            </div>

            {/* Error Message */}
            <div className="space-y-2 items-center">
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-rose-600"
              >
                Payment Failed
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-gray-600"
              >
                {error || "We couldn't process your payment. Please try again or contact support."}
              </motion.p>
            </div>

            {/* Transaction Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-6 space-y-4 bg-gradient-to-r from-red-50 to-rose-50 rounded-2xl"
            >
              <div className="flex justify-between items-center pb-4 border-b border-red-100">
                <span className="text-gray-600">Transaction ID</span>
                <span className="font-medium text-gray-900">{transactionId}</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-red-100">
                <span className="text-gray-600">Package</span>
                <span className="font-medium text-gray-900">{paymentDetails.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Amount</span>
                <span className="text-xl font-bold text-red-600">
                  GH₵{parseFloat(paymentDetails.price).toFixed(2)}
                </span>
              </div>
            </motion.div>

            {/* Common Issues */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="p-6 bg-red-50 rounded-2xl"
            >
              <h3 className="flex justify-center items-center mb-4 font-semibold text-red-900">
                <AlertTriangle className="mr-2 w-5 h-5" />
                Common Issues
              </h3>
              <ul className="space-y-2 text-sm text-left text-red-700">
                <li>• Insufficient funds in your account</li>
                <li>• Network connectivity issues</li>
                <li>• Transaction timeout</li>
                <li>• Payment declined by your Network Provider</li>
              </ul>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-1 "
            >
              <button
                onClick={handleRetry}
                className="flex justify-center items-center px-6 py-4 space-x-2 font-semibold text-white bg-gradient-to-r from-red-500 to-rose-500 rounded-xl transition-all duration-300 hover:shadow-lg"
              >
                <RefreshCcw className="w-5 h-5" />
                <span>Try Again</span>
              </button>
              {/* <button
                onClick={handleSupport}
                className="flex justify-center items-center px-6 py-4 space-x-2 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-all duration-300 hover:bg-gray-200"
              >
                <HelpCircle className="w-5 h-5" />
                <span>Contact Support</span>
              </button> */}
            </motion.div>

            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <button
                onClick={() => navigate('/')}
                className="flex justify-center items-center py-4 space-x-2 w-full text-gray-600 transition-all duration-300 hover:text-gray-900 group"
              >
                <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
                <span>Back to Home</span>
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default PaymentFailed; 